<template>
    <!-- Scroll horizontal en pantallas angostas: el plano nunca baja de un ancho donde se lean las mesas -->
    <div class="fm-scroll">
    <div ref="plane" class="fm-plane" :class="{ 'fm-editable': editable, 'fm-compact': compact }"
         :style="planeStyle">
        <button v-for="t in placedTables" :key="t.id" type="button" class="fm-table"
                :class="['fm-' + (t.shape || 'square'), 'fm-state-' + t.state, {
                    'fm-selected': t.id === selectedId,
                    'fm-dragging': drag && drag.id === t.id && drag.moved,
                    'fm-disabled': isDisabled(t),
                }]"
                :style="boxStyle(t)" :disabled="isDisabled(t)"
                :aria-label="ariaLabel(t)"
                @pointerdown="onPointerDown($event, t)" @pointermove="onPointerMove($event)"
                @pointerup="onPointerUp($event)" @pointercancel="cancelDrag"
                @click="onClick(t)" @keydown="onKeydown($event, t)">
            <span class="fm-inner">
                <span class="fm-name">{{ t.name }}</span>
                <template v-if="!compact">
                    <span v-if="t.state !== 'free' && showTotals" class="fm-total">${{ money(t.total) }}</span>
                    <span v-else-if="t.capacity" class="fm-cap">{{ t.capacity }} pers</span>
                </template>
            </span>
            <span v-if="t.accountCount && !compact" class="fm-bubble"
                  :title="t.accountCount + (t.accountCount === 1 ? ' cuenta' : ' cuentas')">{{ t.accountCount }}</span>
        </button>
        <slot />
    </div>
    </div>
</template>

<script>
import { dragTarget, percentBox, clampPosition, cropView, SNAP } from '../../service/floorLayout';

const GRID_CELL = 40;   // separación de los puntos/líneas del fondo, en unidades lógicas

const STATE_LABELS = { free: 'libre', occupied: 'ocupada', billing: 'por cobrar', reserved: 'reservada' };
const DRAG_THRESHOLD_PX = 4;   // menos que esto es un toque, no un arrastre

export default {
    name: 'FloorMap',
    props: {
        tables: { type: Array, default: () => [] },     // ya filtradas a UNA zona
        canvas: { type: Object, required: true },
        selectedId: { type: Number, default: null },
        editable: { type: Boolean, default: false },    // arrastrar para ubicar (config del salón)
        compact: { type: Boolean, default: false },     // solo nombres (selector de «Mover»)
        disabledIds: { type: Array, default: () => [] },
        // Vista «Todas»: dibuja solo la franja con mesas. Nunca al editar (hay que ver el plano
        // entero para arrastrar a una zona vacía).
        crop: { type: Boolean, default: false },
        showTotals: { type: Boolean, default: true },   // perfil `tables.showTotals`
    },
    emits: ['select', 'drag-end'],
    data() {
        return { drag: null, suppressClick: false };
    },
    computed: {
        view() {
            if (this.crop && !this.editable) return cropView(this.canvas, this.tables);
            return { y: 0, height: this.canvas.height };
        },
        planeStyle() {
            const { width } = this.canvas;
            return {
                aspectRatio: width + ' / ' + this.view.height,
                // El fondo (puntos / grilla) a escala del plano, también cuando está recortado.
                backgroundSize: (GRID_CELL / width) * 100 + '% ' + (GRID_CELL / this.view.height) * 100 + '%',
            };
        },
        // Mientras se arrastra, la mesa se dibuja en su posición en vivo.
        placedTables() {
            if (!this.drag || !this.drag.moved) return this.tables;
            return this.tables.map(t => (t.id === this.drag.id ? { ...t, ...this.drag.pos } : t));
        },
    },
    methods: {
        money(value) { return Number(value || 0).toFixed(2); },
        isDisabled(t) { return this.disabledIds.includes(t.id); },
        ariaLabel(t) {
            const parts = [t.name, STATE_LABELS[t.state] || t.state];
            if (t.accountCount) parts.push(t.accountCount + (t.accountCount === 1 ? ' cuenta' : ' cuentas'));
            if (this.editable) parts.push('flechas para mover');
            return parts.join(', ');
        },
        boxStyle(t) {
            const box = percentBox(this.canvas, t, this.view);
            return { left: box.left + '%', top: box.top + '%', width: box.width + '%', height: box.height + '%' };
        },
        // ── Arrastre (solo en modo edición) ───────────────────────────────────
        onPointerDown(event, table) {
            if (!this.editable || event.button > 0) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            this.drag = { id: table.id, table, startX: event.clientX, startY: event.clientY,
                          moved: false, pos: { posX: table.posX, posY: table.posY } };
        },
        onPointerMove(event) {
            if (!this.drag) return;
            const dx = event.clientX - this.drag.startX;
            const dy = event.clientY - this.drag.startY;
            if (!this.drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
            this.drag.moved = true;
            this.drag.pos = dragTarget(this.canvas, this.drag.table, dx, dy, this.$refs.plane.clientWidth);
        },
        onPointerUp() {
            if (!this.drag) return;
            const { id, moved, pos, table } = this.drag;
            this.drag = null;
            if (moved) {
                this.suppressClick = true;   // el click que sigue al soltar no abre la mesa
                if (pos.posX !== table.posX || pos.posY !== table.posY) this.$emit('drag-end', { id, ...pos });
            }
        },
        cancelDrag() { this.drag = null; },
        onClick(table) {
            if (this.suppressClick) { this.suppressClick = false; return; }
            if (!this.isDisabled(table)) this.$emit('select', table);
        },
        // Teclado (accesible sin mouse): flechas mueven 1 paso de grilla, Shift ×5.
        onKeydown(event, table) {
            if (!this.editable) return;
            const deltas = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
            const d = deltas[event.key];
            if (!d) return;
            event.preventDefault();
            const step = SNAP * (event.shiftKey ? 5 : 1);
            const pos = clampPosition(this.canvas, table.shape, (table.posX || 0) + d[0] * step,
                                      (table.posY || 0) + d[1] * step);
            if (pos.posX !== table.posX || pos.posY !== table.posY) this.$emit('drag-end', { id: table.id, ...pos });
        },
    },
};
</script>

<style scoped>
.fm-scroll { width: 100%; overflow-x: auto; border-radius: 14px; }
.fm-plane { position: relative; width: 100%; min-width: 720px; border-radius: 14px; background-color: var(--surface-50);
    border: 1px solid var(--surface-border); overflow: hidden; user-select: none;
    background-image: radial-gradient(var(--surface-300) 1px, transparent 1px); }
.fm-editable { background-color: var(--surface-0); border-style: dashed; border-color: var(--primary-color);
    background-image: linear-gradient(var(--surface-200) 1px, transparent 1px),
                      linear-gradient(90deg, var(--surface-200) 1px, transparent 1px); }
.fm-table { position: absolute; border: 2px solid transparent; padding: 0; cursor: pointer; font: inherit;
    transition: box-shadow .12s, transform .12s; touch-action: manipulation; }
/* El contenedor de consulta va en un hijo, no en el <button>: sobre el botón, size-containment +
   flex reparte el contenido a los bordes en vez de centrarlo. */
.fm-inner { position: absolute; inset: 0; container-type: size; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 3cqh; line-height: 1.1; }
@container (max-height: 56px) { .fm-total, .fm-cap { display: none; } }
.fm-square { border-radius: 14%; }
.fm-rect { border-radius: 10% / 16%; }
.fm-round { border-radius: 50%; }
.fm-table:hover:not(.fm-disabled) { box-shadow: 0 6px 18px rgba(15, 23, 42, .14); }
.fm-table:focus-visible { outline: 3px solid var(--primary-color); outline-offset: 2px; }
.fm-editable .fm-table { cursor: grab; touch-action: none; border-style: dashed; }
.fm-dragging { cursor: grabbing !important; z-index: 5; box-shadow: 0 12px 28px rgba(15, 23, 42, .25); transform: scale(1.04); }
.fm-selected { outline: 3px solid var(--primary-color); outline-offset: 2px; }
.fm-disabled { opacity: .35; cursor: not-allowed; }
.fm-name { font-weight: 800; font-size: clamp(9px, 15cqh, 18px); line-height: 1.1; max-width: 92%;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fm-total { font-weight: 800; font-size: clamp(9px, 13cqh, 16px); }
.fm-cap { font-size: clamp(8px, 11cqh, 13px); opacity: .75; }
.fm-compact { min-width: 560px; }
.fm-compact .fm-name { font-size: clamp(10px, 20cqh, 15px); }
/* Burbuja en la esquina, por FUERA del borde: así no tapa el nombre. */
.fm-bubble { position: absolute; top: 0; right: 0; transform: translate(40%, -40%); z-index: 1;
    min-width: 20px; height: 20px; border-radius: 999px; background: var(--primary-color);
    color: var(--primary-color-text); font-size: 11px; font-weight: 800; box-shadow: 0 0 0 2px var(--surface-0);
    display: flex; align-items: center; justify-content: center; padding: 0 5px; }
.fm-round .fm-bubble { top: 12%; right: 12%; }
/* Estados (mismos tonos que el panel y el mockup) */
.fm-state-free { background: #f0fdf4; color: #166534; border-color: #86efac; }
.fm-state-occupied { background: #fff7ed; color: #9a3412; border-color: #fdba74; }
.fm-state-billing { background: #eff6ff; color: #1e3a8a; border-color: #93c5fd; }
.fm-state-reserved { background: #f3f4f6; color: #4b5563; border-color: #d1d5db; }
</style>
